// Apolo IDE — Hermes ExecutionPort adapter for the .app (renderer).
//
// Adapts a Hermes (run, agent) pair onto the existing local agent engine:
// runLocalAgentLoop executes tools on the Mac; the BRAIN is local (Ollama via
// IPC) when the agent's model is an ollama registry entry, cloud (Worker
// /agent-turn) otherwise — same split the Code tab and the remote runner use.
//
// This adapter ONLY executes. No state machine, no PATCHes, no store writes —
// the runAgent use case owns the lifecycle (claim/finalize); we just turn a
// claimed run into a loop result `{ ok, finalText, iterations, stopReason }`.

import { resolveBrain as realResolveBrain, resolveBrainRouted } from '../../lib/brainRouter.js';
import { runLocalAgentLoop } from '../../lib/agentLoop.js';
import { defaultToolPolicy } from '../../lib/toolPolicy.js';
import { findModel as realFindModel } from '../../lib/modelRegistry.js';
import { makeRadarEmitTool } from './radarEmitTool.js';
import { makeRadarQueryTool } from './radarQueryTool.js';
import { makeWebBrowseTool } from './webBrowseTool.js';
import { makeWebActTool } from './webActTool.js';
import { isActionTool, classifyActed } from '../domain/runOutcome.js';

// web_act era: a single browser task (one LinkedIn Easy Apply / ATS form) burns
// ~15–30 tool steps (snapshot, fill per field, click, upload, submit). A run is
// expected to complete 20–30 applications, so the ceiling must cover ~30 forms
// plus search/navigation overhead: 40 cut off mid-form, 250 only ~8–15 apps,
// 900 gives real room for a full 20–30-application run to finish and report.
const MAX_ITERATIONS = 900;

// Default recall is a no-op: the adapter NEVER depends on hermesApiClient
// directly (keeps it injectable + the unit tests network-free). runWiring (the
// composition root) injects the real `hermesApi.recallMemory`. With the no-op
// default, an adapter built bare simply runs WITHOUT memory — never throws.
const noRecall = async () => null;

// G4-WS1-T03 — the brain routing + the message assembly (incl. best-effort recall) lifted
// out of the run() method so it stays under the complexity ceiling. Byte-identical.

// Routing insurance: an ollama model is local whether the registry knows it
// (provider === 'ollama') OR only the id says so ('ollama/' prefix) — a local model must
// NEVER be sent to the Worker ("Upstream LLM failed").
function resolveHermesBrain(agentRecord, findModel) {
  const model = agentRecord?.model;
  const isLocal =
    typeof model === 'string' &&
    model.length > 0 &&
    (findModel(model)?.provider === 'ollama' || model.startsWith('ollama/'));
  return isLocal
    ? { kind: 'local', model }
    : { kind: 'cloud', model: model || undefined, adapterId: agentRecord?.adapterId || undefined };
}

// system prompt → recalled memory (D18, best-effort — a recall failure NEVER blocks the
// run) → user task. The backend already applied the FROZEN injection budget.
async function buildRunMessages(agentRecord, runRecord, recallMemory) {
  const messages = [];
  if (agentRecord?.systemPrompt) {
    messages.push({ role: 'system', content: agentRecord.systemPrompt });
  }
  const mem = await recallMemory(agentRecord.id, { query: runRecord.task }).catch(() => null);
  if (typeof mem?.block?.content === 'string' && mem.block.content.trim()) {
    messages.push({ role: 'system', content: mem.block.content });
  }
  messages.push({ role: 'user', content: runRecord.task });
  return messages;
}

export function makeAppExecutionAdapter({
  resolveBrain = realResolveBrain,
  runLoop = runLocalAgentLoop,
  findModel = realFindModel,
  recallMemory = noRecall,
  // radar_emit (write) + radar_query (read) — the authenticated agent↔Radar
  // bridge, injected into EVERY Hermes run via the loop's extraTools seam (the
  // run executes in the renderer, where radarApi already carries the user's
  // JWT). emit writes findings/health/quarantine; query reads sources/findings
  // so a scheduled run discovers what to scan/curate. Injectable (null to omit).
  radarEmitTool = makeRadarEmitTool(),
  radarQueryTool = makeRadarQueryTool(),
  // web_browse (read) — the agent's read-only window onto the live web, injected
  // beside the Radar bridge so EVERY Hermes run can LOOK at a repo/README/docs
  // page or a logged-in feed through the user's own machine (chrome-mcp /
  // embedded BrowserView / public fetch). Read-only, never throws. Injectable
  // (null to omit), defaults to the renderer-bound tool (window.apolo).
  webBrowseTool = makeWebBrowseTool(),
  // web_act (write) — the agent's hands on the live web: drives the user's REAL
  // logged-in Chrome (chrome-mcp) to click/fill/upload/submit so a run can
  // COMPLETE a task on a real site (apply to a job, submit a form), not just
  // read it. mutating:true ⇒ dropped for Team Mode read-only workers and
  // gateable under 'ask'; Hermes runs 'ignore', so it acts autonomously.
  // Injectable (null to omit), defaults to the renderer-bound tool (window.apolo).
  webActTool = makeWebActTool(),
  // E1 (read-only worker mode): when true, the loop drops MUTATING_TOOLS from
  // the defs and hard-denies them at dispatch. Baked at construction — Team
  // Mode's read-only option builds the worker executor with this set, so every
  // operário in that fan-out is analysis-only. Default false ⇒ writes allowed.
  readOnly = false,
} = {}) {
  return {
    /**
     * Execute a claimed run. onEvent receives the loop's live events
     * (token/reasoning/assistant/tool_call/tool_result/done/error/aborted);
     * signal aborts the in-flight loop (Stop / cancel).
     */
    async run(runRecord, agentRecord, { onEvent, signal } = {}) {
      // root comes from the run record (backend snapshots agent.cwd at
      // startRun). Without it the engine has no working directory — fail
      // fast and cheap, before resolving any brain.
      const root = runRecord?.root;
      if (!root || !String(root).trim()) {
        return { ok: false, finalText: '', iterations: 0, stopReason: 'no_root', error: 'agent has no cwd' };
      }

      const brain = resolveHermesBrain(agentRecord, findModel);
      const sessionId = `hermes-${runRecord.id}`;
      // E2 (System One model routing) — additive + flag-gated (apolo_so_route). OFF or
      // explicit-model => identical to resolveBrain. Delegates to the injected resolver.
      const { chatFn, baseUrl, model: resolvedModel } = await resolveBrainRouted(
        brain, runRecord?.task || '', { dispatchId: sessionId }, resolveBrain,
      );

      // Recall runs only AFTER the no-root short-circuit, so we never recall for a run
      // that won't execute.
      const messages = await buildRunMessages(agentRecord, runRecord, recallMemory);

      // E1 honesty: tally EFFECTING tool calls (web_act / run_command / file
      // writes / radar_emit) from the live event stream, so finalize can report
      // whether the run ACTED or only narrated. Wrap the caller's onEvent so the
      // count is a side-effect of the existing stream (no extra plumbing).
      let actionToolCalls = 0;
      const countingOnEvent = (evt) => {
        if (evt && evt.type === 'tool_call' && isActionTool(evt.name)) actionToolCalls += 1;
        return onEvent?.(evt);
      };

      // Hermes subagents run autonomously — there is no approver in the loop,
      // so permissions are 'ignore' (the cockpit's Cancel is the kill switch).
      const result = await runLoop({
        chatFn,
        baseUrl,
        model: resolvedModel,
        root,
        toolPolicy: defaultToolPolicy(), // E0 — deny secret dirs in autonomous Hermes/Radar runs
        messages,
        permissionsMode: 'ignore',
        maxIterations: MAX_ITERATIONS,
        onEvent: countingOnEvent,
        signal,
        taskText: runRecord.task,
        sessionId,
        // The Radar bridge rides every Hermes run — the agent reads its
        // watchlist/findings (radar_query) and persists results (radar_emit)
        // through the user's own session. web_browse rides alongside it so the
        // same run can LOOK at the live web (repo/docs/feed) before it writes.
        extraTools: [radarEmitTool, radarQueryTool, webBrowseTool, webActTool].filter(Boolean),
        // E1 — analysis mode forbids writes/exec for this worker.
        readOnly,
      });

      // E1 honesty: attach the action accounting so the lifecycle's done-PATCH
      // records acted vs narrated. A conversational reply (no tools expected,
      // stopReason conversational_reply/plan_*) counts as acted so a legit Q&A
      // run isn't mislabeled "sem ações".
      const conversational =
        typeof result?.stopReason === 'string' &&
        /conversational|plan_prose|plan_presented/.test(result.stopReason);
      return { ...result, actionToolCalls, acted: classifyActed({ actionToolCalls, conversational }) };
    },
  };
}
